import { chainStores, getDocumentAsStore, getPaginatedDocumentAsStore } from "$lib/utils/firestore";
import { derived, readable, type Readable } from "svelte/store";
import { selectedOrgIdStore, selectedOrgStore } from "./orgs";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { firebaseApp } from "$lib/utils/env";
import { querystring } from "svelte-spa-router";

export const ROOT = Symbol("ROOT");

export interface SensitiveContentCount {
  /** The type of content that is sensitive */
  sensitiveContentType: string;
  /** How sensitive this topic is */
  severityScore: number;
  /** Number of times the sensitive content was detected */
  count: number;
  /** The algorithm that was used to determine the existence of the sensitive content in a given resource */
  sensitivityAlgorithm?: string; // default: "gcp_sdp"
}

export interface DocumentSensitiveContentCount {
  /** Security resource id (e.g. msgraph-v4:document:{connection_id}:{document_id}) */
  resourceId: string;
  /** Name of the resource */
  name: string | Symbol;
  /** Path of the resource */
  path: string;
  /** Link to the resource */
  link: string;
  /** Whether the resource contains sensitive information (e.g. drive with customer PII, document with customer payment details, etc...) */
  isSensitive: boolean;
  /** Content that are sensitive in the resource */
  sensitiveContentCount: SensitiveContentCount[];

  /** Schema version */
  schemaVersion?: string; // default: "v1"
  /** Resource kind */
  resourceKind: "document" | "directory";
}

export interface DirectorySensitiveContentCount extends DocumentSensitiveContentCount {
  /** msgraph-v4:directory:{connection_id}:{hashed_path} */
  resourceId: string;
  /** Number of documents in this directory, resolved recursively through all subfolders. Directories are not included in this count. */
  documentCount: number;
  /** Number of subdirectories under this directory, not including this directory itself */
  subdirectoryCount: number;
  /** Child documents within this directory */
  documentChildren?: DocumentSensitiveContentCount[];
  /** Child subdirectories within this directory */
  subdirectoryChildren?: DirectorySensitiveContentCount[];

  /** Schema version */
  schemaVersion?: string; // default: "v1"
  /** Resource kind */
  resourceKind: "directory";
}

const pathFromUrlStore = readable<string>("/", (set) => {
  let lastPath: string = "/";
  const unsub = querystring.subscribe((qs) => {
    const searchParams = new URLSearchParams(qs);
    const path = searchParams.get("path");
    if (path && path !== lastPath) {
      // We want to update the store when the URL param version changes
      set(path);
      lastPath = path;
    }
  });
  return () => unsub();
});

const hashedPathStore: Readable<string | undefined> = derived([pathFromUrlStore], ([$path], set) => {
  if (!$path || $path === "/") {
    set(undefined);
    return;
  }

  hashPath($path).then(set);
});

export async function hashString(str: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const rootDirStores = derived([selectedOrgStore], ([org], set) => {
  if (!org?.sdp_finding_root_directories) {
    set(undefined);
    return;
  }

  Promise.all(org.sdp_finding_root_directories.map((directory) => hashString(directory))).then(set);
}) as Readable<string[] | undefined>;

async function fetchSensitiveDataByResourceId(
  org_id: string,
  resource: string | DocumentSensitiveContentCount | DirectorySensitiveContentCount,
) {
  if (typeof resource != "string") {
    return resource;
  } else {
    console.log(`[orgSensitiveData] fetchSensitiveDataByResourceId - fetching ${resource} for org ${org_id}`);
    const hashedResourceId = await hashString(resource);
    const docPath = `organisations/${org_id}/sensitive_content_counts/${hashedResourceId}`;
    const docRef = doc(getFirestore(await firebaseApp), docPath);

    // use firestore getDoc instead of a store for each document
    // chaining X number of stores under stores under orgSecuritySensitiveDataStore makes the behaviour really hard to control
    // as each store may update independently, so orgSecuritySensitiveDataStore may update X+1 times
    // what we actually want is to just wait for all children data to be loaded
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.log(`[orgSensitiveData] document not found: ${docPath}`);
      return undefined;
    }
  }
}

export const orgSecuritySensitiveDataStore: Readable<DirectorySensitiveContentCount | undefined> = chainStores(
  derived([hashedPathStore, selectedOrgStore, rootDirStores], (all) => all),
  undefined,
  ([hashedPath, selectedOrg, rootDirs]) => {
    if (!selectedOrg?.id) {
      return readable(undefined);
    }
    const orgId = selectedOrg?.id;

    // We need to do some work here to create a "virtual root" directory. Because there
    // can be multiple root directories for different sources in the same organisation.
    // The root needs to be created to visually show where users can browse.

    if (!hashedPath || hashedPath === "") {
      if (!rootDirs) {
        return readable({
          resourceId: selectedOrg.id,
          name: ROOT,
          documentCount: 0,
          subdirectoryCount: 0,
          documentChildren: [],
          subdirectoryChildren: [],
          isSensitive: false,
          sensitiveContentCount: [],
          schemaVersion: "v1",
          resourceKind: "directory",
          link: "",
          path: "/",
        } as DirectorySensitiveContentCount);
      }

      return derived(
        rootDirs.map((rootDir) =>
          getDocumentAsStore({
            docPath: `organisations/${selectedOrg.id}/sensitive_content_counts/${rootDir}`,
          }),
        ),
        (results) => {
          if (!Array.isArray(results)) {
            return undefined;
          }
          console.log("[orgSensitiveData] root", results);
          if (results.length === 0) {
            return {
              resourceId: selectedOrg.id,
              name: ROOT,
              documentCount: 0,
              subdirectoryCount: 0,
              documentChildren: [],
              subdirectoryChildren: [],
              isSensitive: false,
              sensitiveContentCount: [],
              schemaVersion: "v1",
              resourceKind: "directory",
              link: "",
              path: "/",
            } as DirectorySensitiveContentCount;
          }
          return {
            resourceId: selectedOrg.id,
            name: ROOT,
            documentCount: 0,
            subdirectoryCount: results.reduce((acc, curr) => acc + (curr?.data?.subdirectoryCount || 0), 0),
            documentChildren: [],
            subdirectoryChildren: results
              .map(
                (result) =>
                  ({ ...result?.data, resourceId: result?.data.resourceId! }) as DirectorySensitiveContentCount,
              )
              .filter((result) => result?.resourceId && result?.path),
            isSensitive: results.some((result) => result?.data?.isSensitive),
            sensitiveContentCount: results.reduce(
              (acc, curr) => acc.concat(curr?.data?.sensitiveContentCount || []),
              [],
            ),
            schemaVersion: results[0]?.data?.schemaVersion,
            resourceKind: "directory",
            link: "",
            path: "/",
          } as DirectorySensitiveContentCount;
        },
      );
    }

    const directoryStore = getPaginatedDocumentAsStore({
      docPath: `organisations/${selectedOrg.id}/sensitive_content_counts`,
      docId: hashedPath,
      mergeKeys: ["children"],
      paginationKey: "nextPageId",
    });

    return derived([directoryStore], ([result], set) => {
      if (!result?.id || !result?.data) {
        set(undefined);
      } else if (!result?.data?.resourceId) {
        // fetch from firestore is successful, but no documents found
        console.log(`[orgSensitiveData] could not load ${result?.id}`);
        set({
          resourceId: "UNKNOWN",
          name: "UNKNOWN",
          documentCount: 0,
          subdirectoryCount: 0,
          documentChildren: [],
          subdirectoryChildren: [],
          isSensitive: false,
          sensitiveContentCount: [],
          schemaVersion: "v1",
          resourceKind: "directory",
          link: "",
          path: "",
        } as DirectorySensitiveContentCount);
      } else {
        console.log(
          `[orgSensitiveData] Processing ${result?.data?.children?.length} children for ${result?.data?.resourceId}`,
        );

        const children = Promise.all(
          result.data?.children?.map(
            (resource: string | DirectorySensitiveContentCount | DocumentSensitiveContentCount) =>
              fetchSensitiveDataByResourceId(orgId, resource),
          ),
        );

        children
          .then((resolvedChildren) => {
            set({
              resourceId: result.id,
              ...result.data,
              subdirectoryChildren: resolvedChildren
                ?.filter((child: DirectorySensitiveContentCount) => child.resourceKind === "directory")
                .sort((a, b) =>
                  a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
                ) as DirectorySensitiveContentCount[],
              documentChildren: resolvedChildren
                ?.filter((child: DocumentSensitiveContentCount) => child.resourceKind === "document")
                .sort((a, b) =>
                  a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
                ) as DocumentSensitiveContentCount[],
            } as DirectorySensitiveContentCount);
          })
          .catch((error) => {
            console.error(`Error while resolving children for ${hashedPath}`, error);
            set(undefined);
          });
      }
    }) as Readable<DirectorySensitiveContentCount | undefined>;
  },
);

async function hashPath(path: string) {
  const [provider, ...pathParts] = path.split("/");
  const hashedParts = await Promise.all(pathParts.map((part) => hashString(part)));
  const hashPath = await hashString(hashedParts.join("/"));

  const hashResourceId = await hashString(provider + ":directory::" + hashPath);
  return hashResourceId;
}

// Prefetching of subdirectories to make clicking on subdirectories quicker
derived([orgSecuritySensitiveDataStore, selectedOrgIdStore], ([data, selectedOrgId]) => {
  if (!data || !selectedOrgId) {
    return [];
  }

  if (data?.subdirectoryChildren) {
    // pre-fetch subdirectories by their resourceIds
    // no need to do anything about the results, this is just asking firestore to cache the results
    // caveat: if the subdirectory's children are references (i.e. resourceIds as opposed to json data), these references are not pre-fetched
    // TODO: do we want to pre-fetch subdirectory.children where these are resourceIds?
    return Promise.all(
      data.subdirectoryChildren.map((subdirectory) =>
        fetchSensitiveDataByResourceId(selectedOrgId, subdirectory.resourceId),
      ),
    );
  }

  return [];
}).subscribe(async (data) => {
  // This will run whenever the orgSecuritySensitiveDataStore or selectedOrgIdStore changes, we need a subscription to init the store
  // and trigger the fetchSubdirectoryData function
  // but we don't need to do anything here

  // Log only if data promise resolves

  console.log("[orgSecuritySensitiveDataStore] Subdirectory data pre-fetched", (await data).length);
});

orgSecuritySensitiveDataStore.subscribe((data) => {
  // This will run whenever the orgSecuritySensitiveDataStore changes
  // but we don't need to do anything here

  // Log only if data promise resolves
  console.log("[orgSecuritySensitiveDataStore] Data updated", data);
});
